import React, {ReactElement} from "react";
import {
  Button,
  ButtonBase,
  Card,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography
} from "@mui/material";
import {BORDER_RADIUS, PD_MD, PD_SM, PD_XLG, SZ_MD, SZ_SM, SZ_SMSM, SZ_SSM, SZ_XSM} from "./dimens";
import {TableData, TableDataMetadata, tableDataMetadataKey} from "./tabledata";
import {
  BrokenImageOutlined,
  Favorite,
  FavoriteBorderOutlined,
  KeyboardArrowDownOutlined,
  KeyboardArrowUpOutlined,
  SettingsOutlined,
  Star,
  StarOutlineOutlined
} from "@mui/icons-material";
import {BaseApp} from "./BaseApp";
import {Action, CreateActions, Render, Renderable} from "./types";
import {StyledBoxColumn, StyledBoxRow, StyledTableBodyCell} from "./StyledComponents";
import {findIcon} from "./icons";
import Markdown from "react-markdown";
import {brightPaletteColors} from "./colors";
import {md5_code} from "./md5";
import {PathProps} from "./index";

export type DataTableRowViewProps<T, D> = {
  path: PathProps,
  data: T,
  row: D,
  index: number,
  contentItemsMap: Map<string, TableDataContentItem>,
  disableSelection?: boolean,
  onTableCellChanged?: (data: T, cellId: string, value: any) => void,
  onTableDataSelected: (data: T) => void,
  onMultiSelectionChanged: (id: string) => void,
}

const CELL_SIZE_MAX_WIDTHS = {
  "small": 48,
  "medium": 96,
  "large": 192,
  "xlarge": 240,
  "xxlarge": 480,
  "full": null,
};

type DataTableRowViewState = {
  open?: boolean,
}

class DataTableRowView<T, D extends TableDataRow> extends React.Component<DataTableRowViewProps<T, D>, DataTableRowViewState> {

  constructor(props: DataTableRowViewProps<T, D>, context: any) {
    super(props, context);
    this.state = {};
  }

  componentDidUpdate(prevProps: Readonly<DataTableRowViewProps<T, D>>, prevState: Readonly<DataTableRowViewState>, snapshot?: any) {
    if (prevProps.data !== this.props.data) {
      this.setState({
        open: false,
      });
    }
  }

  render() {
    const data = this.props.data;
    const row = this.props.row;
    // We need to wrap the row in a div, otherwise the "position: relative" style doesn't work in Safari.
    // <div style={{width: "100%", position: "relative"}}>
    return <>
      <TableRow hover style={{position: "relative", height: SZ_SM}}>
        {Object.getOwnPropertyNames(row).map(value => {
          const cell = row[value];
          if (typeof cell === "function") {
            return null;
          }
          const contentItem = this.props.contentItemsMap.get(value);
          if (!contentItem) {
            return null;
          }
          let cellStyle = {};
          const cellSize = contentItem.metadata.cellSize;
          if (cellSize) {
            cellStyle = {
              ...cellStyle,
              flexShrink: 0,
              width: CELL_SIZE_MAX_WIDTHS[cellSize],
            };
          }
          if (contentItem.metadata.type === "profile_photo") {
            return <TableCell style={{width: 0, paddingTop: 0, paddingBottom: 0}}>
              <Card style={{width: SZ_SM, height: SZ_SM, flexShrink: 0}}>
                <img src={cell || BaseApp.CONTEXT.getAppConfig().defaultUserImage}
                     style={{width: "100%", height: "100%"}}/>
              </Card>
            </TableCell>;
          } else if (contentItem.metadata.type === "icon") {
            const IconType = findIcon(cell) || BrokenImageOutlined;
            return <TableCell style={{width: 0, paddingLeft: PD_SM, paddingRight: PD_SM}}>
              <IconType style={{width: SZ_XSM, height: SZ_XSM, flexShrink: 0}}/>
            </TableCell>;
          } else if (contentItem.metadata.type === "checkbox") {
            return <TableCell style={{width: SZ_MD, flexGrow: 0, flexShrink: 0, paddingLeft: PD_MD}}>
              {this.renderCheckbox(cell, contentItem, row, value, data)}
            </TableCell>;
          } else if (contentItem.metadata.type === "expand") {
            return <TableCell style={{width: SZ_XSM}}>
              <IconButton
                size="small"
                style={{zIndex: 1000}}
                onClick={(event) => {
                  event.preventDefault();
                  this.setState({open: !this.state.open});
                }}
              >
                {this.state.open ? <KeyboardArrowUpOutlined/> : <KeyboardArrowDownOutlined/>}
              </IconButton>
            </TableCell>;
          } else if (contentItem.metadata.type === "color") {
            return <TableCell style={{width: SZ_MD, flexGrow: 0, flexShrink: 0}}>
              <Button disabled style={{width: SZ_SM, height: SZ_SSM, backgroundColor: cell}}/>
            </TableCell>;
          } else if (contentItem.metadata.type === "actions") {
            const IconType = contentItem.metadata.actionsIcon || SettingsOutlined;
            return <TableCell style={{width: SZ_MD, flexGrow: 0, flexShrink: 0}}>
              <Button disabled={contentItem.metadata.disabled} style={{zIndex: 1000}} onClick={(event) => {
                event.preventDefault();
                let actions: Action[];
                if (Array.isArray(contentItem.metadata.actions)) {
                  actions = contentItem.metadata.actions as Action[];
                } else {
                  actions = (contentItem.metadata.actions as CreateActions)(
                    event,
                    {
                      path: this.props.path,
                      data: data,
                    });
                }
                BaseApp.CONTEXT.showActionsDialog("Actions", actions, data);
              }}>
                {<IconType/>}
              </Button>
            </TableCell>;
          } else if (contentItem.metadata.type === "custom") {
            return <TableCell style={contentItem.metadata.cellStyle}>{cell}</TableCell>;
          } else if (contentItem.metadata.type === "enum") {
            return <TableCell style={{
              paddingLeft: PD_MD,
              ...cellStyle,
              ...contentItem.metadata.cellStyle
            }}>
              <StyledBoxRow>
                <Typography style={{
                  overflowX: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  paddingLeft: PD_MD,
                  paddingRight: PD_MD,
                  color: contentItem.metadata.enumColors?.find(kt => kt.key === cell)?.metadata.color || contentItem.metadata.enumDefaultColor?.color,
                  background: contentItem.metadata.enumColors?.find(kt => kt.key === cell)?.metadata.background || contentItem.metadata.enumDefaultColor?.background,
                  borderRadius: BORDER_RADIUS,
                }}>{contentItem.metadata.enumValues?.find(kt => kt.key === cell)?.text}</Typography>
              </StyledBoxRow>
            </TableCell>;
          } else if (contentItem.metadata.type === "rich_text") {
            return <TableCell style={{
              paddingLeft: PD_MD,
              height: SZ_SSM,
              ...contentItem.metadata.cellStyle
            }}>
              <Typography>
                <Markdown className="rich_text_cell">
                  {cell as string}
                </Markdown>
              </Typography>
            </TableCell>;
          } else if (contentItem.metadata.type === "tagged_text") {
            return <TableCell style={{
              paddingLeft: PD_MD,
              height: SZ_SSM,
              ...cellStyle,
              ...contentItem.metadata.cellStyle
            }}>
              <Typography>
                {this.renderTaggedText(cell as string)}
              </Typography>
            </TableCell>;
          } else {
            let text;
            if (typeof cell === "string") {
              text = cell as string;
            } else if (cell instanceof Date) {
              const date = cell as Date;
              text = date.toISOString();
            } else {
              text = cell?.toString() || "";
            }
            return <StyledTableBodyCell style={{
              ...cellStyle,
              ...contentItem.metadata.cellStyle
            }}>
              {text}
            </StyledTableBodyCell>;
          }
        }).filter(Boolean)}
        {this.props.disableSelection
          ? null
          : <ButtonBase
            key={`row-${this.props.index}`}
            style={{position: "absolute", width: "100%", top: 0, left: 0, bottom: 0, right: 0}}
            onClick={() => this.props.onTableDataSelected(data)}/>
        }
      </TableRow>
      {this.state.open
        ? <TableRow>
          <TableCell colSpan={100}>
            {Render(row["__expandRenderable"], this.props.path)}
          </TableCell>
        </TableRow>
        : null}
    </>;
  }

  private renderCheckbox(cell: D, contentItem: TableDataContentItem, row: D, value: string, data: T) {
    let icon = undefined;
    let checkedIcon = undefined;
    switch (contentItem.metadata.checkboxVariant) {
      default:
      case "checkbox":
        break;
      case "star":
        icon = <StarOutlineOutlined/>;
        checkedIcon = <Star/>;
        break;
      case "heart":
        icon = <FavoriteBorderOutlined/>;
        checkedIcon = <Favorite/>;
        break;
    }
    return <Checkbox
      style={{padding: 0}}
      checked={Boolean(cell)} disabled={contentItem.metadata.disabled}
      icon={icon}
      checkedIcon={checkedIcon}
      onChange={(event, checked) => {
        row[value] = checked;
        this.forceUpdate();
        this.props.onTableCellChanged?.(data, contentItem.metadata.cellId, checked);
        if (contentItem.metadata._propertyKey === "__selected") {
          this.props.onMultiSelectionChanged(value);
        }
      }}/>;
  }

  private renderTaggedText(text: string): ReactElement {
    if (!text) {
      return null;
    }
    let transformed: ReactElement[] = [];
    let start, end = -1;
    while ((start = text.indexOf("[", end + 1)) >= 0) {
      transformed.push(<>{text.substring(end + 1, start)}</>);
      if ((end = text.indexOf("]", start + 1)) > 0) {
        const substring = text.substring(start + 1, end);
        transformed.push(<span style={{
          color: brightPaletteColors[md5_code(substring) % brightPaletteColors.length],
          fontWeight: "bold"
        }}>{" " + substring}</span>);
      } else break;
    }
    transformed.push(<>{text.substring(end + 1)}</>);
    return <Typography
      style={{
        overflowX: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
      }}>
      {transformed}
    </Typography>
  }
}

// Marker interface for table data row objects
// A "style" field in subclasses will be applied to row view.
export interface TableDataRow {
}

export abstract class BaseSelectTableDataRow implements TableDataRow {

  @TableData({name: "-", type: "checkbox", multiSelect: true})
  __selected: void;
}

export abstract class BaseExpandTableDataRow<T> implements TableDataRow {

  @TableData({name: "", type: "expand"})
  __expand: void;

  abstract expandRenderable(item: T): Renderable;
}

export type TableDataSource<T, D extends TableDataRow> = {

  createTableDataRow(): D,

  applyTableDataToRow(data: T, row: D): void,

  onTableDataSelected(data: T): void;

  onTableCellChanged?: (data: T, cellId: string, value: any) => void;
}

export type TableDataContainerSelectModeConfig<T> = {
  initialSelection?: T[],
}

export interface TableDataContainerMultiSelectListener<T> {
  onSelectionChanged(items: T[], exitSelection: () => void): void;
}

export type GroupingMetadata = {
  id: string,
  title?: string,
}

export const DEFAULT_GROUPING: GroupingMetadata = {id: "_default"};

class GroupedData<T, D extends TableDataRow> {

  contentRows: any[] = [];
  selectAll?: boolean;

  constructor(readonly data: T[] = [], readonly groupingMetadata: GroupingMetadata = DEFAULT_GROUPING) {
  }
}

export type TableDataProvider<T> = (from: number, to?: number) => T[];

export type TableDataContainerProps<T, D extends TableDataRow> = {
  path?: PathProps,
  data: T[] | TableDataProvider<T>,
  dataTotal?: number,
  pageSize?: number,
  hideHeader?: boolean,
  groupingFn?: (item: T) => GroupingMetadata,
  source: TableDataSource<T, D>,
  disableSelection?: boolean,
  selectModeConfig?: TableDataContainerSelectModeConfig<T>,
  multiSelectListener?: TableDataContainerMultiSelectListener<T>,
  displayMode?: "standard" | "condensed",
}

type TableDataContentItem = {
  id: string,
  target: any,
  metadata: TableDataMetadata,
}

type TableDataContainerState<T, D extends TableDataRow> = {
  contentItems: TableDataContentItem[];
  groups: GroupedData<T, D>[],
  selectedGroups?: GroupedData<T, D>[],
  pageIndex: number,
  pageSize: number,
}

const DEFAULT_PAGE_SIZE = 25;

export class TableDataContainer<T, D extends TableDataRow> extends React.Component<TableDataContainerProps<T, D>, TableDataContainerState<T, D>> {

  private readonly contentItemsMap = new Map<string, TableDataContentItem>();

  constructor(props: TableDataContainerProps<T, D>, context: any) {
    super(props, context);
    this.state = {
      contentItems: [],
      groups: [],
      pageIndex: 0,
      pageSize: this.props.pageSize || DEFAULT_PAGE_SIZE,
    }
  }

  componentDidMount() {
    this.updateContent();
  }

  componentDidUpdate(prevProps: Readonly<TableDataContainerProps<T, D>>, prevState: Readonly<TableDataContainerState<T, D>>, snapshot?: any) {
    if (prevProps.data !== this.props.data || prevProps.source !== this.props.source || prevProps.groupingFn !== this.props.groupingFn || prevState.pageIndex !== this.state.pageIndex || prevProps.selectModeConfig?.initialSelection !== this.props.selectModeConfig?.initialSelection) {
      this.updateContent();
    }
  }

  private updateContent() {
    const row = this.props.source.createTableDataRow();
    const contentItems = this.maybeSelectPreferredItems(this.findContentItems(row));
    this.contentItemsMap.clear();
    contentItems.forEach(contentItem => this.contentItemsMap.set(contentItem.id, contentItem));
    const groups: GroupedData<T, D>[] = [];
    let data: T[];
    if (Array.isArray(this.props.data)) {
      data = this.props.data;
    } else if (typeof this.props.data === "function") {
      data = ((this.props.data) as TableDataProvider<T>)(this.state.pageIndex * this.state.pageSize, this.props.dataTotal ? Math.min(this.props.dataTotal, (this.state.pageIndex + 1) * this.state.pageSize) : undefined);
    } else {
      data = [];
    }
    if (this.props.groupingFn) {
      const map = new Map<string, GroupedData<T, D>>();
      for (const item of data) {
        const groupingMetadata = this.props.groupingFn(item) || DEFAULT_GROUPING;
        let groupedData = map.get(groupingMetadata.id);
        if (!groupedData) {
          groupedData = new GroupedData<T, D>([], groupingMetadata);
          map.set(groupingMetadata.id, groupedData);
        }
        groupedData.data.push(item);
        this.updateContentRow(item, row, contentItems, groupedData);
      }
      groups.push(...map.values());
    } else {
      const groupedData = new GroupedData<T, D>(data);
      data.forEach(item => {
        this.updateContentRow(item, row, contentItems, groupedData);
      });
      groups.push(groupedData);
    }
    this.setState({
      contentItems: contentItems,
      groups: groups,
    });
  }

  private maybeSelectPreferredItems(contentItems: TableDataContentItem[]): TableDataContentItem[] {
    if (contentItems.length > 4 && this.props.displayMode === "condensed") {
      let preferredItems: TableDataContentItem[] = [];
      for (const item of contentItems) {
        if (item.metadata.preferInCondensedMode) {
          preferredItems.push(item)
        }
      }
      let nonPreferredItemsCount = Math.max(0, 4 - preferredItems.length);
      let newContentItems: TableDataContentItem[] = [];
      for (const item of contentItems) {
        if (item.metadata.preferInCondensedMode) {
          newContentItems.push(item);
        } else if (nonPreferredItemsCount > 0) {
          newContentItems.push(item);
          nonPreferredItemsCount--;
        }
      }
      return newContentItems;
    }
    return contentItems;
  }

  private updateContentRow(item: T, row: D, contentItems: TableDataContentItem[], groupedData: GroupedData<T, D>) {
    const value = {};
    this.props.source.applyTableDataToRow(item, row);
    contentItems.forEach(contentItem => value[contentItem.id] = row[contentItem.metadata._propertyKey]);
    if (this.props.selectModeConfig?.initialSelection && row instanceof BaseSelectTableDataRow) {
      value["__selected"] = this.props.selectModeConfig.initialSelection.includes(item);
    }
    if (row instanceof BaseExpandTableDataRow) {
      value["__expandRenderable"] = row.expandRenderable(item);
    }
    groupedData.contentRows.push(value);
  }

  private findContentItems(object: D): TableDataContentItem[] {
    let contentItems: TableDataContentItem[] = [];
    // if (!object) {
    //   return contentItems;
    // }
    if (object instanceof Array) {
      const array: any[] = object;
      array.forEach(item => contentItems.push(...this.findContentItems(item)));
    } else {
      for (let propertyKey of Object.getOwnPropertyNames(object)) {
        //@ts-ignore
        let metadata: TableDataMetadata = object[tableDataMetadataKey(propertyKey)];
        if (!metadata) {
          continue;
        }
        const contentItem = {id: propertyKey, target: object, metadata: metadata};
        contentItems.push(contentItem);
      }
    }
    return contentItems;
  }

  render() {
    return <StyledBoxColumn
      style={{
        gap: PD_XLG,
        padding: PD_SM,
        listStyleType: "none",
      }}>
      {this.state.groups.map(group => {
        return <Card style={{overflowX: "scroll"}}>
          <Table>
            {!this.props.hideHeader
              ? <TableHead>
                <TableRow>
                  {this.state.contentItems.map(value => <TableCell style={{height: SZ_SMSM, paddingLeft: PD_MD}}>
                    {this.renderHeaderCell(value, group)}
                  </TableCell>)}
                </TableRow>
              </TableHead>
              : null}
            <TableBody>
              {group.contentRows?.map((row, index) => <DataTableRowView
                path={this.props.path}
                data={group.data[index]}
                row={row}
                index={index}
                contentItemsMap={this.contentItemsMap}
                disableSelection={Boolean(this.props.selectModeConfig) || this.props.disableSelection}
                onTableCellChanged={(data, cellId, value) => this.props.source.onTableCellChanged(data, cellId, value)}
                onTableDataSelected={data => this.props.source.onTableDataSelected(data)}
                onMultiSelectionChanged={(id: string) => this.onMultiSelectChanged(group, id)}
              />)}
            </TableBody>
          </Table>
          {this.props.dataTotal
            ? <TablePagination
              component="div"
              count={this.props.dataTotal}
              rowsPerPage={this.state.pageSize}
              page={this.state.pageIndex}
              onPageChange={(event, page) => {
                this.setState({
                  pageIndex: page,
                });
              }}
              onRowsPerPageChange={() => {
              }}
            />
            : null}
        </Card>
      })}
    </StyledBoxColumn>;
  }

  private renderHeaderCell(value: TableDataContentItem, group: GroupedData<T, D>) {
    if (value.metadata._propertyKey === "__selected" && value.metadata.multiSelect) {
      return <Checkbox
        style={{padding: 0}}
        disabled={value.metadata.disabled}
        checked={Boolean(group.selectAll)}
        onChange={(event, checked) => {
          group.contentRows.forEach(item => item[value.id] = checked);
          this.onMultiSelectChanged(group, value.id);
        }}/>;
    } else if (value.metadata._propertyKey === "__expand") {
      return "";
    } else {
      return <b>{value.metadata.name}</b>;
    }
  }

  private onMultiSelectChanged(group: GroupedData<T, D>, id: string) {
    let selectAll = true;
    const items: T[] = group.contentRows.reduce((result: T[], currentValue, currentIndex, array) => {
      if (Boolean(currentValue[id])) {
        result.push(group.data[currentIndex]);
      } else {
        selectAll = false;
      }
      return result;
    }, []);
    group.selectAll = selectAll;
    this.setState({
      selectedGroups: this.state.groups?.filter(group => group.selectAll),
    });
    this.props.multiSelectListener?.onSelectionChanged(items,
      () => {
        group.contentRows.forEach(item => item[id] = false);
        group.selectAll = false;
        this.setState({
          selectedGroups: this.state.groups?.filter(group => group.selectAll),
        });
        this.onMultiSelectChanged(group, id);
      });
  }
}
